/**
 * Agent Methods Display Component
 *
 * Shows agent methods and capabilities:
 * - Method name and description
 * - Input/output parameters
 * - Cost and performance metrics
 * - Usage examples
 * - Execute button with parameter inputs
 * - Status monitoring and results display
 *
 * Follows: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  CodeBracketIcon,
  ArrowRightIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon as SpinnerIcon
} from '@heroicons/react/24/outline';
import { Badge, Card, Button, TextInput, Spinner } from 'flowbite-react';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

export interface AgentMethod {
  name: string;
  description: string;
  category?: string;
  parameters?: any;
  inputs?: {
    name: string;
    type: string;
    required: boolean;
    description?: string;
  }[];
  outputs?: {
    name: string;
    type: string;
    description?: string;
  }[];
  metrics?: {
    avgCost?: number;
    avgDuration?: number;
    successRate?: number;
    callCount?: number;
  };
  example?: string;
  preferredModel?: string;
  estimatedCost?: number;
  estimatedDuration?: number;
}

interface MethodExecution {
  executionId: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'timeout';
  progress: number;
  result?: any;
  error?: string;
  actualCost?: number;
  actualDuration?: number;
  startTime?: string;
  endTime?: string;
}

interface AgentMethodsDisplayProps {
  agentId: string;
  agentName: string;
  methods: AgentMethod[];
  isLoading?: boolean;
  error?: string;
}

const AgentMethodsDisplay: React.FC<AgentMethodsDisplayProps> = ({
  agentId,
  agentName,
  methods,
  isLoading = false,
  error
}) => {
  const [expandedMethods, setExpandedMethods] = useState<Set<string>>(new Set());
  const [methodParameters, setMethodParameters] = useState<Record<string, any>>({});
  const [executions, setExecutions] = useState<Record<string, MethodExecution>>({});
  const [isExecuting, setIsExecuting] = useState<Record<string, boolean>>({});

  const toggleMethod = (methodName: string) => {
    const newExpanded = new Set(expandedMethods);
    if (newExpanded.has(methodName)) {
      newExpanded.delete(methodName);
    } else {
      newExpanded.add(methodName);
    }
    setExpandedMethods(newExpanded);
  };

  /**
   * Execute agent method
   */
  const executeMethod = async (method: AgentMethod) => {
    const methodKey = `${agentId}_${method.name}`;
    setIsExecuting({ ...isExecuting, [methodKey]: true });

    try {
      // Get parameters from state
      const params = methodParameters[methodKey] || {};

      // Get multi-tenant context from localStorage
      const companyId = localStorage.getItem('selectedCompanyId');
      const projectId = localStorage.getItem('selectedProjectId');

      // Call execute API with multi-tenant context for cost tracking
      const response = await Make_Authorized_API_Call<{ execution_id: string }>(
        API_ENDPOINTS.AGENTS.EXECUTE,
        'POST',
        {
          agentId,
          methodName: method.name,
          parameters: params,
          companyId,
          projectId,
        }
      );

      if (isSuccessResponse(response)) {
        const executionId = response.data?.Records?.[0]?.execution_id;

        if (executionId) {
          // Initialize execution state
          setExecutions({
            ...executions,
            [methodKey]: {
              executionId,
              status: 'queued',
              progress: 0,
            },
          });

          // Start polling for status
          pollExecutionStatus(methodKey, executionId);
        }
      } else {
        console.error('Execution failed:', response.message);
      }
    } catch (err) {
      console.error('Error executing method:', err);
    } finally {
      setIsExecuting({ ...isExecuting, [methodKey]: false });
    }
  };

  /**
   * Poll execution status
   */
  const pollExecutionStatus = useCallback(
    async (methodKey: string, executionId: string) => {
      const pollInterval = setInterval(async () => {
        try {
          // TODO: The API_ENDPOINTS.AGENTS.EXECUTION_STATUS is /api/agents/execution
          // which requires Execution_ID as query param. Using endpoint + param pattern.
          const response = await Make_Authorized_API_Call<{
            status: 'queued' | 'running' | 'completed' | 'failed' | 'timeout';
            progress: number;
            result?: any;
            error_message?: string;
            actual_cost?: number;
            actual_duration?: number;
            start_time?: string;
            end_time?: string;
            is_terminal: boolean;
          }>(
            API_ENDPOINTS.AGENTS.EXECUTION_STATUS,
            'GET',
            undefined,
            { params: { Execution_ID: executionId } }
          );

          if (isSuccessResponse(response) && response.data?.Records?.[0]) {
            const executionData = response.data.Records[0];

            setExecutions((prev) => ({
              ...prev,
              [methodKey]: {
                executionId,
                status: executionData.status,
                progress: executionData.progress,
                result: executionData.result,
                error: executionData.error_message,
                actualCost: executionData.actual_cost,
                actualDuration: executionData.actual_duration,
                startTime: executionData.start_time,
                endTime: executionData.end_time,
              },
            }));

            // Stop polling if terminal state
            if (executionData.is_terminal) {
              clearInterval(pollInterval);
            }
          }
        } catch (err) {
          console.error('Error polling execution status:', err);
          clearInterval(pollInterval);
        }
      }, 2000); // Poll every 2 seconds

      // Clear interval after 5 minutes max
      setTimeout(() => clearInterval(pollInterval), 300000);
    },
    [executions]
  );

  /**
   * Update parameter value
   */
  const updateParameter = (methodKey: string, paramName: string, value: any) => {
    setMethodParameters({
      ...methodParameters,
      [methodKey]: {
        ...methodParameters[methodKey],
        [paramName]: value,
      },
    });
  };

  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-800">Error loading methods: {error}</p>
      </div>
    );
  }

  if (!methods || methods.length === 0) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">No methods available for this agent</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <CodeBracketIcon className="h-6 w-6 text-indigo-600" />
        <h3 className="text-lg font-semibold text-gray-900">
          {agentName} Methods ({methods.length})
        </h3>
      </div>

      <div className="space-y-3">
        {methods.map((method) => {
          const isExpanded = expandedMethods.has(method.name);
          const methodKey = `${agentId}_${method.name}`;
          const execution = executions[methodKey];
          const executing = isExecuting[methodKey];

          return (
            <Card key={method.name} className="hover:shadow-md transition-shadow">
              {/* Method Header */}
              <button
                onClick={() => toggleMethod(method.name)}
                className="w-full text-left"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="text-sm font-mono font-semibold text-indigo-900">
                        {method.name}()
                      </code>
                      {method.category && (
                        <Badge color="gray" size="sm">
                          {method.category}
                        </Badge>
                      )}
                      {execution && (
                        <Badge
                          color={
                            execution.status === 'completed'
                              ? 'success'
                              : execution.status === 'failed'
                              ? 'failure'
                              : execution.status === 'running'
                              ? 'info'
                              : 'warning'
                          }
                          size="sm"
                        >
                          {execution.status}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{method.description}</p>
                  </div>
                  {isExpanded ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500 ml-2" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500 ml-2" />
                  )}
                </div>
              </button>

              {/* Metrics Summary */}
              {method.metrics && (
                <div className="mt-3 grid grid-cols-4 gap-3">
                  {method.metrics.avgCost !== undefined && (
                    <div className="flex items-center gap-2">
                      <CurrencyDollarIcon className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500">Avg Cost</p>
                        <p className="text-sm font-semibold">
                          ${method.metrics.avgCost.toFixed(4)}
                        </p>
                      </div>
                    </div>
                  )}

                  {method.metrics.avgDuration !== undefined && (
                    <div className="flex items-center gap-2">
                      <ClockIcon className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500">Avg Duration</p>
                        <p className="text-sm font-semibold">
                          {formatDuration(method.metrics.avgDuration)}
                        </p>
                      </div>
                    </div>
                  )}

                  {method.metrics.successRate !== undefined && (
                    <div className="flex items-center gap-2">
                      <ChartBarIcon className="h-4 w-4 text-green-500" />
                      <div>
                        <p className="text-xs text-gray-500">Success Rate</p>
                        <p className="text-sm font-semibold">
                          {(method.metrics.successRate * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  )}

                  {method.metrics.callCount !== undefined && (
                    <div className="flex items-center gap-2">
                      <ChartBarIcon className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500">Call Count</p>
                        <p className="text-sm font-semibold">
                          {method.metrics.callCount.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Expanded Details */}
              {isExpanded && (
                <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
                  {/* Execute Button */}
                  <div className="flex items-center gap-3 mb-4">
                    <Button
                      color="indigo"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        executeMethod(method);
                      }}
                      disabled={executing || (execution && execution.status === 'running')}
                    >
                      {executing || (execution && execution.status === 'running') ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          Executing...
                        </>
                      ) : (
                        <>
                          <PlayIcon className="h-4 w-4 mr-2" />
                          Execute Method
                        </>
                      )}
                    </Button>

                    {method.estimatedCost && (
                      <span className="text-xs text-gray-500">
                        Est. Cost: ${method.estimatedCost.toFixed(2)}
                      </span>
                    )}
                    {method.estimatedDuration && (
                      <span className="text-xs text-gray-500">
                        Est. Duration: {formatDuration(method.estimatedDuration)}
                      </span>
                    )}
                  </div>

                  {/* Execution Status */}
                  {execution && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {execution.status === 'completed' && (
                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                          )}
                          {execution.status === 'failed' && (
                            <XCircleIcon className="h-5 w-5 text-red-600" />
                          )}
                          {(execution.status === 'running' || execution.status === 'queued') && (
                            <Spinner size="sm" />
                          )}
                          <span className="text-sm font-medium">
                            Execution ID: {execution.executionId}
                          </span>
                        </div>
                        <Badge
                          color={
                            execution.status === 'completed'
                              ? 'success'
                              : execution.status === 'failed'
                              ? 'failure'
                              : 'info'
                          }
                        >
                          {execution.status}
                        </Badge>
                      </div>

                      {/* Progress Bar */}
                      {execution.progress !== undefined && execution.status !== 'completed' && (
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${execution.progress}%` }}
                          />
                        </div>
                      )}

                      {/* Error Message */}
                      {execution.error && (
                        <div className="bg-red-50 border border-red-200 rounded p-3">
                          <p className="text-sm text-red-800">{execution.error}</p>
                        </div>
                      )}

                      {/* Result */}
                      {execution.result && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Result</p>
                          <pre className="bg-gray-900 text-gray-100 rounded p-3 text-xs overflow-x-auto max-h-64">
                            {JSON.stringify(execution.result, null, 2)}
                          </pre>
                        </div>
                      )}

                      {/* Actual Metrics */}
                      {execution.actualCost !== undefined || execution.actualDuration !== undefined ? (
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-blue-300">
                          {execution.actualCost !== undefined && (
                            <div>
                              <p className="text-xs text-gray-600">Actual Cost</p>
                              <p className="text-sm font-semibold">${execution.actualCost.toFixed(4)}</p>
                            </div>
                          )}
                          {execution.actualDuration !== undefined && (
                            <div>
                              <p className="text-xs text-gray-600">Actual Duration</p>
                              <p className="text-sm font-semibold">
                                {formatDuration(execution.actualDuration)}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}

                  {/* Input Parameters */}
                  {method.inputs && method.inputs.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Input Parameters</p>
                      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        {method.inputs.map((input) => (
                          <div key={input.name} className="flex items-start gap-2">
                            <code className="text-xs font-mono bg-white px-2 py-1 rounded border border-gray-200">
                              {input.name}: {input.type}
                            </code>
                            {input.required && (
                              <Badge color="failure" size="xs">
                                required
                              </Badge>
                            )}
                            {input.description && (
                              <span className="text-xs text-gray-600">{input.description}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Output Parameters */}
                  {method.outputs && method.outputs.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <ArrowRightIcon className="h-4 w-4 text-gray-500" />
                        <p className="text-sm font-medium text-gray-700">Output Parameters</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        {method.outputs.map((output) => (
                          <div key={output.name} className="flex items-start gap-2">
                            <code className="text-xs font-mono bg-white px-2 py-1 rounded border border-gray-200">
                              {output.name}: {output.type}
                            </code>
                            {output.description && (
                              <span className="text-xs text-gray-600">{output.description}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Usage Example */}
                  {method.example && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Usage Example</p>
                      <pre className="bg-gray-900 text-gray-100 rounded-lg p-3 text-xs overflow-x-auto">
                        <code>{method.example}</code>
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default AgentMethodsDisplay;
